import { SimpleGrid, Box, Text, Flex, Badge, Tag } from '@chakra-ui/react';
import {
  RAG_COLORS,
  formatCurrency,
  formatRatio,
  type RAGStatus,
} from 'utils/rag';

interface OverallMetrics {
  score: number;
  p2p_percent: number;
  p2p_goal: number;
  p2p_pace: number;
  pace2p_pl: number;
  pace2p_cl: number;
  pace2p_lf: number;
  pace2p_pc?: number | null;
  avg_leads_per_day_r30: number;
  avg_leads_per_broker_per_day_r30?: number;
  nb_premium_total: number;
  nb_premium_pl: number;
  nb_premium_cl: number;
  nb_premium_lf: number;
  closing_ratio: number;
  broker_productivity?: number;
  rag_score: RAGStatus;
  rag_p2p: RAGStatus;
  rag_closing: RAGStatus;
}

interface KPICardProps {
  title: string;
  primary: string;
  subItems?: { label: string; value: string }[];
  rag?: RAGStatus;
  badge?: string;
  isPlaceholder?: boolean;
}

export const KPICard = ({
  title,
  primary,
  subItems,
  rag,
  badge,
  isPlaceholder,
}: KPICardProps) => {
  const ragColor = rag && !isPlaceholder ? RAG_COLORS[rag] : null;

  return (
    <Box
      bg="white"
      borderRadius="16px"
      p="20px"
      boxShadow="0px 2px 8px rgba(0,0,0,0.08)"
      borderLeft={ragColor ? `4px solid ${ragColor.text}` : '4px solid #E2E8F0'}
      minH="110px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      opacity={isPlaceholder ? 0.45 : 1}
    >
      <Text
        fontSize="xs"
        fontWeight="600"
        color="gray.500"
        textTransform="uppercase"
        mb="6px"
      >
        {title}
      </Text>
      <Flex align="baseline" gap="8px" mb="8px">
        <Text
          fontSize="2xl"
          fontWeight="700"
          color={ragColor?.text || 'gray.800'}
          lineHeight="1.2"
        >
          {primary}
        </Text>
        {badge && (
          <Badge
            colorScheme={isPlaceholder ? 'gray' : 'orange'}
            fontSize="xs"
            flexShrink={0}
          >
            {badge}
          </Badge>
        )}
      </Flex>
      {subItems && subItems.length > 0 && (
        <Flex gap="12px" wrap="wrap">
          {subItems.map((item) => (
            <Box key={item.label} minW="48px">
              <Text fontSize="xs" color="gray.400">
                {item.label}
              </Text>
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                {item.value}
              </Text>
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
};

interface Props {
  data: OverallMetrics;
  label?: string;
}

export const OverallKPICards = ({ data, label }: Props) => {
  const hasData =
    data.score > 0 || data.nb_premium_total > 0 || data.p2p_percent > 0;

  return (
    <>
      {label && (
        <Flex align="center" gap="8px" mb="12px">
          <Text
            fontSize="xs"
            fontWeight="600"
            color="gray.500"
            textTransform="uppercase"
          >
            Showing KPIs for
          </Text>
          <Tag size="sm" colorScheme="brand" borderRadius="6px">
            {label}
          </Tag>
        </Flex>
      )}

      {!hasData ? (
        <Box
          bg="gray.50"
          borderRadius="12px"
          border="1px dashed"
          borderColor="gray.200"
          py="32px"
          textAlign="center"
          mb="24px"
        >
          <Text fontSize="sm" color="gray.400">
            No Data Available
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="16px" mb="24px">
          <KPICard
            title="Sales Score"
            primary={data.score ? String(Math.round(data.score)) : '—'}
            rag={data.score ? data.rag_score : undefined}
          />

          <KPICard
            title="New Business Premium"
            primary={formatCurrency(data.nb_premium_total)}
            subItems={[
              { label: 'PL', value: formatCurrency(data.nb_premium_pl) },
              { label: 'CL', value: formatCurrency(data.nb_premium_cl) },
              { label: 'L&F', value: formatCurrency(data.nb_premium_lf) },
            ]}
          />

          <KPICard
            title="Performance to Plan"
            primary={`${Math.round(data.p2p_percent)}%`}
            rag={data.rag_p2p}
            subItems={[
              { label: 'Goal', value: formatCurrency(data.p2p_goal) },
              { label: 'Pace', value: `${Math.round(data.p2p_pace)}%` },
              ...(data.pace2p_pc != null
                ? [{ label: 'P&C', value: `${Math.round(data.pace2p_pc)}%` }]
                : [
                    {
                      label: 'PL',
                      value: `${Math.round(data.pace2p_pl)}%`,
                    },
                    {
                      label: 'CL',
                      value: `${Math.round(data.pace2p_cl)}%`,
                    },
                  ]),
              {
                label: data.pace2p_pc != null ? 'Life' : 'LF',
                value: `${Math.round(data.pace2p_lf)}%`,
              },
            ]}
          />

          <KPICard title="Retention" primary="—" isPlaceholder />

          <KPICard
            title="Quotes Per Day"
            primary={
              data.avg_leads_per_day_r30
                ? data.avg_leads_per_day_r30.toFixed(1)
                : '—'
            }
            badge="30 day avg"
            subItems={[
              {
                label: 'Per Broker',
                value: data.avg_leads_per_broker_per_day_r30
                  ? data.avg_leads_per_broker_per_day_r30.toFixed(1)
                  : '—',
              },
            ]}
          />

          <KPICard
            title="Closing Ratio"
            primary={formatRatio(data.closing_ratio)}
            rag={data.rag_closing}
          />

          <KPICard
            title="Broker Productivity"
            primary={
              data.broker_productivity
                ? formatCurrency(data.broker_productivity)
                : '—'
            }
            badge="30 day rolling"
          />

          <KPICard
            title="Book of Business Growth Rate"
            primary="—"
            isPlaceholder
          />
        </SimpleGrid>
      )}
    </>
  );
};
